import React from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { formatJst } from "../lib/date";
import { jobHealthClass, deriveRunCounts } from "../lib/jobStatusFormat";
import { useLocalization } from "../hooks/useLocalization";

/** Detail drawer for one Background Job. Recent run history + the last error (sanitized -
 * error_summary only, never the raw stack trace) are enough for an admin to diagnose a
 * failure without ever needing server-log access. */
export const JobDetailDrawer = ({ job, history, open, onOpenChange, canManage, onRunNow, isRunning }) => {
  const { t } = useLocalization();
  if (!job) return null;
  const counts = deriveRunCounts(job.lastRun);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="job-detail-drawer" className="overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{job.label}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={jobHealthClass(job.displayStatus)} data-testid="job-detail-status">
              {t(`jobHealth.${job.displayStatus}`)}
            </Badge>
            <Badge variant="secondary">{t(`jobMode.${job.mode}`)}</Badge>
            <span className="text-xs text-gray-500">{job.schedule}</span>
          </div>

          <p className="text-sm text-gray-600">{job.purpose}</p>

          {job.enabled && (
            <>
              <dl className="grid grid-cols-2 gap-3 text-sm border-t border-gray-100 pt-4">
                <div><dt className="text-gray-500">{t("systemJobs.lastStarted")}</dt><dd>{job.lastRun ? formatJst(job.lastRun.started_at) : "—"}</dd></div>
                <div><dt className="text-gray-500">{t("systemJobs.lastCompleted")}</dt><dd>{job.lastRun?.completed_at ? formatJst(job.lastRun.completed_at) : "—"}</dd></div>
                <div><dt className="text-gray-500">{t("systemJobs.duration")}</dt><dd>{job.lastRun?.duration_ms != null ? `${job.lastRun.duration_ms} ms` : "—"}</dd></div>
                <div><dt className="text-gray-500">{t("systemJobs.nextRun")}</dt><dd>{job.nextRun ? formatJst(job.nextRun) : "—"}</dd></div>
                <div><dt className="text-gray-500">{t("systemJobs.processed")}</dt><dd>{counts.processed}</dd></div>
                <div><dt className="text-gray-500">{t("systemJobs.succeeded")}</dt><dd>{counts.succeeded}</dd></div>
              </dl>

              {job.lastRun?.status === "FAILED" && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="job-detail-error">
                  <p className="text-xs font-semibold text-red-700 mb-1">{t("systemJobs.lastError")}</p>
                  <p className="text-sm text-red-800 break-words">{job.lastRun.error_summary}</p>
                  <Link to="/management/action-center" data-testid="job-detail-action-center-link" className="text-xs text-red-700 underline mt-2 inline-block">
                    {t("systemJobs.viewActionCenter")}
                  </Link>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{t("systemJobs.recentRuns")}</p>
                {history.length === 0 ? (
                  <p className="text-sm text-gray-400">{t("systemJobs.noRunsYet")}</p>
                ) : (
                  <ul className="space-y-1.5 text-sm" data-testid="job-run-history-list">
                    {history.map((run) => (
                      <li key={run.id} className="flex items-center justify-between border-b border-gray-100 pb-1.5">
                        <span className="text-gray-600">{formatJst(run.started_at)}</span>
                        <span className={run.status === "SUCCESS" ? "text-emerald-600" : "text-red-600"}>
                          {t(`jobRunStatus.${run.status}`)} · {run.triggered_by?.startsWith("MANUAL") ? t("systemJobs.manual") : t("systemJobs.scheduled")}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {canManage && job.manualTriggerAllowed && (
                <Button
                  data-testid="job-detail-run-now-button"
                  className="w-full"
                  disabled={job.displayStatus === "RUNNING" || isRunning}
                  onClick={onRunNow}
                >
                  {job.displayStatus === "RUNNING" ? t("systemJobs.currentlyRunning") : t("systemJobs.runNow")}
                </Button>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
